import { useParams } from "react-router-dom";
import { DocumentTextIcon } from "@heroicons/react/24/solid";

import { useArrangementAttestation } from "hooks/useArrangementAPI";

import { useArrangement } from "./ArrangementContext";
import HandoffPanel from "./HandoffPanel";
import { formatPersonName } from "./helpers";

/**
 * Shown when the viewer is not the purchaser. A single attestation authorizes
 * them to continue on the purchaser's behalf — covering both signing the
 * documents and confirming payment. On success `onAttested` fires and the
 * parent renders the gated action (the signing embed or the payment UI).
 */
export default function ContinueOnBehalfPanel({
  onAttested,
}: {
  onAttested: () => void;
}) {
  const { caseId } = useParams<{ caseId: string }>();
  const { purchaser } = useArrangement();
  const { trigger: recordAttestation, isMutating } =
    useArrangementAttestation(caseId);

  const purchaserFirstName = purchaser?.first_name?.trim() || "the purchaser";
  const purchaserFullName = formatPersonName(purchaser, "the purchaser");

  // The documents go out on whichever channel we hold for the purchaser —
  // email when there is an address, otherwise a text (doc-service routes
  // recipients with only a phone to SMS). Naming the wrong one sends them
  // hunting through an inbox nothing was ever sent to.
  const handoffChannel = purchaser?.email
    ? "email"
    : purchaser?.phone
      ? "text"
      : null;

  return (
    <HandoffPanel
      icon={DocumentTextIcon}
      heading={`${purchaserFirstName} is the purchaser on this arrangement.`}
      body={
        <>
          If you&apos;re completing it on their behalf, continue below — you can
          sign the documents and confirm payment.
          {handoffChannel
            ? ` Otherwise, ${purchaserFirstName} can finish from the ${handoffChannel} we sent them.`
            : null}
        </>
      }
      primaryLabel="Continue on behalf of purchaser"
      attestModalTitle="Continue on the purchaser's behalf"
      attestModalBody={
        <>
          I confirm that {purchaserFullName} has authorized me to complete this
          arrangement on their behalf, including signing the required documents
          and confirming payment information. I attest that this is true and
          accurate.
        </>
      }
      attestConfirmLabel="Continue"
      isMutating={isMutating}
      onAttest={async () => {
        await recordAttestation({});
        onAttested();
      }}
    />
  );
}
